import { Dispatch, SetStateAction, useEffect, useState } from "react"

import {
  Box,
  SkeletonText,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
} from "@chakra-ui/react"
import { format } from "date-fns"
import { useParams } from "react-router-dom"

import CustomCheckBox from "components/customChakra/CustomCheckBox"
import CustomTableRow from "components/customChakra/CustomTableRow"
import CustomTh from "components/customChakra/CustomTh"
import { ASC, DESC, SortOrder } from "pages/fee-mapping/FeeMapping"
import { CategoryData } from "services/common"

export interface FeeMappingItem {
  blocked: boolean
  id: string
}

interface FeeMappingTableTypeProps {
  categoryData: CategoryData[] | undefined
  isLoading: boolean
  pageSize: number
  setUpdatedCategoryData: Dispatch<SetStateAction<FeeMappingItem[]>>
  onSort: Dispatch<
    SetStateAction<{
      key: keyof CategoryData | "wsFeeId" | null
      order: SortOrder
    }>
  >
}

const SkeletonTableRow = () => (
  <Tr>
    <Td>
      <SkeletonText noOfLines={1} w='200px' />
    </Td>
    <Td>
      <SkeletonText noOfLines={1} w='200px' />
    </Td>
    <Td>
      <SkeletonText noOfLines={1} w='100px' />
    </Td>
    <Td>
      <SkeletonText noOfLines={1} w='100px' />
    </Td>
    <Td>
      <SkeletonText noOfLines={1} w='20px' />
    </Td>
    <Td>
      <SkeletonText noOfLines={1} w='200px' />
    </Td>
  </Tr>
)
const FeeNameTable = (props: FeeMappingTableTypeProps) => {
  const { categoryData, isLoading, pageSize, setUpdatedCategoryData, onSort } =
    props

  const [checkedItems, setCheckedItems] = useState<string[]>([])

  const { blockedStatus } = useParams()

  const handleSort = (key: keyof CategoryData | "wsFeeId") => {
    onSort((prevConfig) => {
      if (prevConfig.key === key) {
        return { key, order: prevConfig.order === ASC ? DESC : ASC }
      }
      return { key, order: ASC }
    })
  }

  useEffect(() => {
    if (categoryData) {
      // setCategories(initialCategories)

      const initiallyCheckedItems = categoryData
        .filter((data) => data.blocked)
        .map((data) => data.feeName)
      setCheckedItems(initiallyCheckedItems)
    }
  }, [categoryData])

  useEffect(() => {
    const generateFeeMappingData = () => {
      const mappingData = (categoryData || []).reduce(
        (accumulator: FeeMappingItem[], data) => {
          const originallyBlocked = data.blocked
          const currentlyBlocked = checkedItems.includes(String(data.id))

          // Check if the feeCategory or blocked status has changed
          const blockedStatusChanged = originallyBlocked !== currentlyBlocked

          if (blockedStatusChanged) {
            accumulator.push({
              blocked: currentlyBlocked,
              id: data.id,
            })
          }

          return accumulator
        },
        [],
      )

      return mappingData
    }

    const newMappingData = generateFeeMappingData()

    setUpdatedCategoryData((prevData) => {
      // If there are changes, update the state with the new data
      if (JSON.stringify(prevData) !== JSON.stringify(newMappingData)) {
        return newMappingData
      }
      return prevData
    })
  }, [checkedItems, categoryData, setUpdatedCategoryData])

  useEffect(() => {
    if (categoryData) {
      const initiallyCheckedItems = categoryData
        .filter((data) => data.blocked)
        .map((data) => data.id)
      setCheckedItems(initiallyCheckedItems)
    }
  }, [categoryData])

  const formatFeeAmount = (amount: number): string => {
    const [integerPart, decimalPart] = amount.toString().split(".")

    if (decimalPart && decimalPart.length > 4) {
      return `${integerPart}.${decimalPart.slice(0, 4)}`
    }

    return amount.toString()
  }

  return (
    <Box
      display='flex'
      flexDir='column'
      gap='20px'
      mr={{ base: "-20px", md: "0" }}
      mb={4}
    >
      <Box
        w={{
          base: "100vw",
          lg2: "calc(100vw - 316px)",
          xl: "calc(100vw - 410px)",
        }}
        maxWidth='full'
        overflow='auto'
        position='relative'
      >
        <TableContainer
          borderBottom='1px solid #e1e1e1'
          pr={{ base: "20px", md: "0" }}
          overflowY='auto'
          maxHeight={{
            base: "55vh",
            sm: "60vh",
            md: "70vh",
            lg2: "50vh",
            xxl: "60vh",
          }}
        >
          <Table>
            <Thead position='sticky' top={0} zIndex={1} background='white'>
              <CustomTableRow>
                <CustomTh isSortable onClick={() => handleSort("wsFeeId")}>
                  Whimstay Fee ID
                </CustomTh>
                <CustomTh>Fee Type</CustomTh>
                <CustomTh isSortable onClick={() => handleSort("feeAmount")}>
                  Fee Amount
                </CustomTh>
                <CustomTh>Amount Type</CustomTh>

                <CustomTh>Block fee</CustomTh>
                <CustomTh display={{ base: "none", md: "block" }}>
                  Date Modified
                </CustomTh>
              </CustomTableRow>
            </Thead>
            <Tbody>
              {!isLoading &&
                categoryData?.map((data) => (
                  <CustomTableRow key={data?.id}>
                    <Td fontWeight={500}>{data?.id || "-"}</Td>
                    <Td fontWeight={500}>{data?.type || "-"}</Td>
                    <Td fontWeight={500}>
                      {data.feeType === "PERCENT"
                        ? `${formatFeeAmount(Number(data.feeAmount) * 100)}%`
                        : `$${formatFeeAmount(Number(data.feeAmount))}`}
                    </Td>

                    <Td fontWeight={500}>{data.feeType.replace("_", " ")}</Td>

                    <Td fontWeight={500}>
                      <CustomCheckBox
                        isDisabled={
                          data.feeCategory === null || blockedStatus === "true"
                        }
                        isChecked={checkedItems.includes(String(data.id))}
                        onChange={(e) => {
                          e.preventDefault()
                          e.stopPropagation()

                          setCheckedItems((ids) => {
                            const updatedIds = ids.includes(String(data.id))
                              ? ids.filter((x) => x !== String(data.id))
                              : [...ids, String(data.id)]

                            return updatedIds
                          })
                        }}
                        borderColor='#464646'
                      />
                    </Td>
                    <Td
                      fontWeight={500}
                      display={{ base: "none", md: "block" }}
                    >
                      {data.modifiedDate ? (
                        format(new Date(data.modifiedDate), "MM/dd/yyyy")
                      ) : (
                        <span>-</span>
                      )}
                    </Td>
                  </CustomTableRow>
                ))}

              {isLoading &&
                Array(pageSize)
                  .fill(1)
                  .map((x: number, ind) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <SkeletonTableRow key={`skeleton${x}${ind}`} />
                  ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}

export default FeeNameTable
